import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import type { AnnotationLabelT, AnswerT, RangeSpanT } from '../../../../../common/types/types';
import { TEMPORAL_SCOPE } from '../../../../../modernization-common/relationships/constants';
import { LabelQuestionnaire } from '../../../../../modernization-common/side-panel';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import type { ModernizedGenericAnnotation, SidePanelDispatchAtom } from '../../../../../modernization-common/side-panel/types';
import type { AnnotationT } from '../../../../labelling/types';
import { frameCountAtom } from '../../../atoms/annotation-atoms';
import { currentFrameAtom } from '../../../atoms/video-playback-atoms';
import { FrameClassificationPanelFooter } from './frame-classification-panel-footer';
import { FrameClassificationFrameRangeInputs } from './frame-range-inputs';
import { InstanceScopeSelect } from './instance-scope-select';
import { getInitialTemporalScope, validateFrameRange } from './utils';
type FrameClassificationPanelProps = {
  annotationLabel: AnnotationLabelT;
  annotationId: string;
  existingSpan?: RangeSpanT;
  existingAnswers?: AnswerT;
  dispatchAtom: SidePanelDispatchAtom<AnnotationT & ModernizedGenericAnnotation>;
  onClose: () => void;
};
export const FrameClassificationPanel = ({
  annotationLabel,
  annotationId,
  existingSpan,
  existingAnswers,
  dispatchAtom,
  onClose
}: FrameClassificationPanelProps) => {
  const [css, theme] = useStyletron();
  const dispatch = useSetAtom(dispatchAtom);
  const frameCount = useAtomValue(frameCountAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const isEditMode = !!existingSpan;
  const [temporalScope, setTemporalScope] = useState(() => getInitialTemporalScope(existingSpan));
  const [startFrame, setStartFrame] = useState<number | undefined>(existingSpan ? existingSpan.start : currentFrame);
  const [endFrame, setEndFrame] = useState<number | undefined>(existingSpan ? existingSpan.end : currentFrame);
  const [answers, setAnswers] = useState<AnswerT | undefined>(existingAnswers);
  const [isAnswerValid, setIsAnswerValid] = useState(true);
  const {
    startError,
    endError
  } = validateFrameRange(startFrame, endFrame, temporalScope, frameCount);
  const questionnaireId = annotationLabel.questionnaire?.[0]?.questionnaireId ?? '';
  const canSave = isAnswerValid && !startError && !endError && startFrame !== undefined;
  const handleTemporalScopeChange = (nextTemporalScope: typeof temporalScope) => {
    setTemporalScope(nextTemporalScope);
    if (nextTemporalScope === TEMPORAL_SCOPE.INSTANT) {
      setEndFrame(startFrame);
    }
  };
  const handleSave = () => {
    if (!canSave || startFrame === undefined) {
      return;
    }
    const newSpan: RangeSpanT = {
      start: startFrame,
      end: temporalScope === TEMPORAL_SCOPE.INTERVAL ? endFrame ?? startFrame : startFrame
    };
    dispatch({
      type: SIDE_PANEL_ACTIONS.APPLY_FRAME_CLASSIFICATION_SPAN,
      annotationIdx: annotationId,
      annotationLabel,
      answers: answers ?? {},
      frameSpan: newSpan
    });
    onClose();
  };
  return <div className={css({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  })}>
      <div className={css({
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      padding: theme.sizing.scale600,
      gap: theme.sizing.scale300
    })}>
        <div>
          {questionnaireId && <LabelQuestionnaire questionnaireId={questionnaireId} answers={answers ?? {}} style={{
          padding: 0
        }} onChange={(isValid, updatedAnswers) => {
          setIsAnswerValid(isValid);
          setAnswers(updatedAnswers);
        }} />}
        </div>
        <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.sizing.scale600
      })}>
          <InstanceScopeSelect value={temporalScope} onChange={handleTemporalScopeChange} />

          <FrameClassificationFrameRangeInputs temporalScope={temporalScope} startFrame={startFrame} endFrame={endFrame} startError={startError} endError={endError} onStartFrameChange={setStartFrame} onEndFrameChange={setEndFrame} startFrameTooltip={`The first frame where this ${annotationLabel.title} applies.`} endFrameTooltip={`The last frame where this ${annotationLabel.title} applies.`} />
        </div>
      </div>

      <FrameClassificationPanelFooter annotationLabelTitle={annotationLabel.title} isEditMode={isEditMode} canSave={canSave} onCancel={onClose} onSave={handleSave} />
    </div>;
};